import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAccount } from '../components/AccountLayout';
import { StatTile } from '../components/RpcUi';
import { Select } from '../components/Select';
import { daysBetween, UsageChart, type UsageMetric } from '../components/UsageChart';
import { chainName, RPC_CHAINS } from '../lib/chains';
import { count, usd } from '../lib/money';
import {
  meteredBy,
  rpcApi,
  type GroupUsage,
  type MethodUsage,
  type UsageResponse,
  type UsageTotals,
} from '../lib/rpc-client';
import { SaasError } from '../lib/saas-client';

const RANGES = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function TotalsTable({
  title,
  rows,
  testid,
}: {
  title: string;
  rows: { id: string; label: string; t: UsageTotals }[];
  testid: string;
}) {
  return (
    <section className="panel">
      <div className="panel-eyebrow">{title}</div>
      {rows.length === 0 ? (
        <p className="muted">No usage in this range.</p>
      ) : (
        <div className="rpc-table-wrap">
          <table className="key-table rpc-num-table" data-testid={testid}>
            <thead>
              <tr>
                <th />
                <th>Requests</th>
                <th>Units</th>
                <th>Refused</th>
                <th>Proofs served</th>
                <th>Charged</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ id, label, t }) => (
                // By id: two revoked keys can share a label.
                <tr key={id}>
                  <td>{label}</td>
                  <td>{count(t.served_requests)}</td>
                  <td>{count(t.served_units)}</td>
                  <td>{count(t.refused)}</td>
                  <td>{count(t.proof_served)}</td>
                  <td>{usd(t.charged_micros)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Served usage by the method group whose price was charged. */
function GroupTable({ rows }: { rows: GroupUsage[] }) {
  return (
    <section className="panel">
      <div className="panel-eyebrow">By method group</div>
      <div className="rpc-table-wrap">
        <table className="key-table rpc-num-table" data-testid="usage-by-group">
          <thead>
            <tr>
              <th />
              <th>Requests</th>
              <th>Units</th>
              <th>Charged</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((g) => (
              <tr key={g.group_id ?? ''}>
                <td>{g.group_id ? `${g.group_id}. ${g.name ?? g.group_id}` : 'Other requests'}</td>
                <td>{count(g.served_requests)}</td>
                <td>{count(g.served_units)}</td>
                <td>{usd(g.charged_micros)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Served usage by method, most requests first; the group column on plans priced by group. */
function MethodTable({ rows, grouped }: { rows: MethodUsage[]; grouped: boolean }) {
  return (
    <section className="panel">
      <div className="panel-eyebrow">By method</div>
      <div className="rpc-table-wrap">
        <table className="key-table rpc-num-table" data-testid="usage-by-method">
          <thead>
            <tr>
              <th />
              {grouped && <th>Group</th>}
              <th>Requests</th>
              <th>Units</th>
              <th>Charged</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr key={`${m.method ?? ''}:${m.group_id ?? ''}`}>
                <td>{m.method ?? <span className="muted">Not broken down</span>}</td>
                {grouped && <td>{m.group_id ?? 'Other'}</td>}
                <td>{count(m.served_requests)}</td>
                <td>{count(m.served_units)}</td>
                <td>{usd(m.charged_micros)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted rpc-small">
        Requests sent before the RPC reported methods, and a state method's answers billed after
        its request ended, are not broken down.
      </p>
    </section>
  );
}

export function UsagePanel() {
  const { keys, summary } = useAccount();
  const [range, setRange] = useState(30);
  const [keyId, setKeyId] = useState('');
  const [chain, setChain] = useState('');
  const [metric, setMetric] = useState<UsageMetric>('served_units');
  // A plan billed by request opens on requests, until the chart is switched by hand.
  const picked = useRef(false);
  const billed = summary ? meteredBy(summary.plan) : null;
  const postpaid = summary?.plan.postpaid === true;
  const grouped = (summary?.rates?.groups.length ?? 0) > 0;
  useEffect(() => {
    if (!picked.current && billed === 'requests') setMetric('served_requests');
  }, [billed]);
  const pick = (m: UsageMetric) => {
    picked.current = true;
    setMetric(m);
  };
  const [data, setData] = useState<UsageResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { from, to } = useMemo(() => {
    const end = new Date();
    const start = new Date(end);
    start.setUTCDate(start.getUTCDate() - (range - 1));
    return { from: isoDay(start), to: isoDay(end) };
  }, [range]);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(
        await rpcApi.usage({
          from,
          to,
          key_id: keyId || undefined,
          chain: chain ? Number(chain) : undefined,
        }),
      );
    } catch (e) {
      setError(e instanceof SaasError ? e.message : 'Could not load usage.');
    }
  }, [from, to, keyId, chain]);

  useEffect(() => {
    load();
  }, [load]);

  const keyLabel = useCallback(
    (id: string) => {
      const k = keys?.find((x) => x.key_id === id);
      // Usage keeps rows for keys that were revoked or rotated away. saas lists
      // the most recently revoked by name; older ones show by id.
      if (!k) return `${id.slice(0, 12)}… (revoked)`;
      return k.revoked_at ? `${k.name} (revoked)` : k.name;
    },
    [keys],
  );

  const days = useMemo(() => (data ? daysBetween(data.from, data.to) : []), [data]);
  const t = data?.total;

  return (
    <>
      <section className="panel rpc-filters" aria-label="Usage filters">
        <div className="rpc-segmented" role="group" aria-label="Range">
          {RANGES.map((r) => (
            <button
              key={r.days}
              type="button"
              className={range === r.days ? 'is-active' : undefined}
              aria-pressed={range === r.days}
              onClick={() => setRange(r.days)}
              data-testid={`range-${r.days}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <label className="rpc-field rpc-field--inline">
          <span>Key</span>
          <Select
            value={keyId}
            onChange={setKeyId}
            options={[
              { value: '', label: 'All keys' },
              ...(keys ?? []).map((k) => ({ value: k.key_id, label: k.name })),
            ]}
            data-testid="usage-key"
          />
        </label>
        <label className="rpc-field rpc-field--inline">
          <span>Chain</span>
          <Select
            value={chain}
            onChange={setChain}
            options={[
              { value: '', label: 'All chains' },
              ...RPC_CHAINS.map((c) => ({ value: String(c.id), label: c.name })),
            ]}
            data-testid="usage-chain"
          />
        </label>
      </section>

      {error && <div className="rpc-notice rpc-notice--error">{error}</div>}

      <section className="rpc-stats" aria-label="Usage totals">
        <StatTile label="Requests served" value={t ? count(t.served_requests) : '…'} testid="usage-requests" />
        <StatTile
          label="Units"
          value={t ? count(t.served_units) : '…'}
          sub={t && t.billed_units > 0 ? `${count(t.billed_units)} past your plan's units` : null}
          testid="usage-units"
        />
        <StatTile label="Refused" value={t ? count(t.refused) : '…'} sub="Not billed" testid="usage-refused" />
        <StatTile label="Proofs served" value={t ? count(t.proof_served) : '…'} testid="usage-proofs" />
        <StatTile
          label="Charged"
          value={t ? usd(t.charged_micros) : '…'}
          sub={postpaid ? 'Invoiced monthly' : 'Units past your plan'}
          testid="usage-charged"
        />
      </section>

      <section className="panel">
        <div className="rpc-chart-head">
          <div>
            <div className="panel-eyebrow">Per day (UTC)</div>
            <h2>{metric === 'served_units' ? 'Units' : 'Requests'} served</h2>
          </div>
          <div className="rpc-segmented" role="group" aria-label="Metric">
            <button
              type="button"
              className={metric === 'served_units' ? 'is-active' : undefined}
              aria-pressed={metric === 'served_units'}
              onClick={() => pick('served_units')}
            >
              Units
            </button>
            <button
              type="button"
              className={metric === 'served_requests' ? 'is-active' : undefined}
              aria-pressed={metric === 'served_requests'}
              onClick={() => pick('served_requests')}
              data-testid="metric-requests"
            >
              Requests
            </button>
          </div>
        </div>
        {data ? (
          <UsageChart days={days} byDay={data.by_day} metric={metric} />
        ) : (
          <p className="muted">Loading…</p>
        )}
        <p className="muted rpc-small" data-testid="usage-note">
          {billed === 'requests' ? (
            <>
              Your plan bills each request served (each item of a batch is one); refused
              requests are free. The RPC reports usage every 10 seconds.
            </>
          ) : (
            <>
              A unit is up to 4 KB of gzipped answer, and every request served costs at least
              one. The RPC reports usage every 10 seconds. Proof charges are in the ledger on the
              Billing page.
            </>
          )}
        </p>
      </section>

      {data && (
        <>
          <TotalsTable
            title="By key"
            testid="usage-by-key"
            rows={Object.entries(data.by_key).map(([id, tt]) => ({ id, label: keyLabel(id), t: tt }))}
          />
          <TotalsTable
            title="By chain"
            testid="usage-by-chain"
            rows={Object.entries(data.by_chain).map(([id, tt]) => ({ id, label: chainName(id), t: tt }))}
          />
          {grouped && data.by_group && data.by_group.length > 0 && <GroupTable rows={data.by_group} />}
          {data.by_method && data.by_method.length > 0 && <MethodTable rows={data.by_method} grouped={grouped} />}
          <TotalsTable
            title="By day"
            testid="usage-by-day"
            rows={Object.entries(data.by_day)
              .sort(([a], [b]) => (a < b ? 1 : -1))
              .map(([d, tt]) => ({ id: d, label: d, t: tt }))}
          />
        </>
      )}
    </>
  );
}
